import TuneIcon from "@mui/icons-material/Tune";
import { Box, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { ICON_SIZE } from "../../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction, PbjPredictionJobCoverageGap } from "../../../../utils/models";
import { needsPredictionSetup, SETUP_STEP_LABEL_KEY, SETUP_STEPS } from "./predictionSetup.utils";
import { SetupFacilityStep } from "./SetupFacilityStep";
import { SetupManualHoursStep } from "./SetupManualHoursStep";
import { SetupTurnoverStep } from "./SetupTurnoverStep";

type Props = {
	open: boolean;
	prediction: PbjPrediction;
	draft: PbjModelUpsertBody;
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute. Called once, on Finish, so a half-filled flow never writes a config.
	onCalculate: (next: PbjModelUpsertBody) => void;
	// Close without finishing. The caller keeps the department on the national-average CMI and puts
	// the flow behind a banner, so the page stays navigable and the setup is still one click away.
	onDismiss: () => void;
	startDate: string;
	endDate: string;
	missingJobs: PbjPredictionJobCoverageGap[] | undefined;
};

/**
 * The one-time setup a department walks through before its first prediction: the facility's CMI,
 * its turnover measures, and the hours the score is built from. Only step 1 gates Next; steps 2 and
 * 3 carry values that are genuinely optional (CASPER supplies turnover, and a department with no
 * salaried nurses has no hours to add).
 *
 * It closes on ✕, Escape and the backdrop. It used to have no way out at all, on the reasoning that
 * a prediction scored against a national-average CMI nobody chose is a wrong number presented as a
 * right one — but a modal with no exit also traps the whole app: the header tabs, the other PBJ
 * sub-tabs and the department picker are all behind its backdrop, so a user who opened the
 * Prediction tab to read the Audit tab had to reload the page. Closing does not answer the CMI
 * question; `PredictionSetupBanner` keeps it on screen, and the score stays labelled as the seed.
 */
export const PredictionSetupDialog: FC<Props> = ({ open, prediction, draft, onDraftChange, onCalculate, onDismiss, startDate, endDate, missingJobs }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const theme = useTheme();
	const [activeStep, setActiveStep] = useState(0);
	// Whether the manual CMI pair on step 1 could actually be saved. True while the manual fields are
	// closed — then the CCN is the answer and there is no pair to judge.
	const [manualCmiValid, setManualCmiValid] = useState(true);

	// The dialog outlives a department switch (the page keeps it mounted), so a flow abandoned on
	// step 3 for one department would greet the next one on step 3 — past the facility question it
	// has not answered. Start every department at the beginning.
	const departmentId = prediction.config.departmentId;
	// biome-ignore lint/correctness/useExhaustiveDependencies: the department IS the reset trigger; the setters are stable.
	useEffect(() => {
		setActiveStep(0);
		setManualCmiValid(true);
	}, [departmentId]);

	const step = SETUP_STEPS[activeStep];
	const isLastStep = activeStep === SETUP_STEPS.length - 1;
	// Step 1 is the reason the flow exists: until CMI has a source — and, on the manual path, a
	// saveable one — Next stays closed.
	const canAdvance = step !== "facility" || (!needsPredictionSetup(draft) && manualCmiValid);

	const handleDismiss = (): void => {
		track("pbj_model_setup_dismissed", {
			department_id: prediction.config.departmentId,
			step,
		});
		onDismiss();
	};

	const handleNext = (): void => {
		if (!isLastStep) {
			setActiveStep((previous) => previous + 1);
			return;
		}
		track("pbj_model_setup_completed", {
			department_id: prediction.config.departmentId,
			cmi_source: draft.ccn === null ? "manual" : "ccn",
			missing_jobs_count: missingJobs?.length ?? 0,
		});
		onCalculate(draft);
	};

	return (
		<SystemDialog
			open={open}
			// ✕, backdrop and Escape all route here — the one way out that isn't Finish.
			onClose={handleDismiss}
			maxWidth="sm"
			fullWidth
			hideCancel
			showCloseIcon
			dataTestId="pbj-model-setup-dialog"
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<TuneIcon sx={{ fontSize: ICON_SIZE.PILL, color: theme.palette.primary.main }} />
					<Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
						{t("pbj_model.setup.title")}
					</Typography>
				</Box>
			}
			confirm={t(isLastStep ? "pbj_model.setup.finish" : "pbj_model.setup.next")}
			confirmButtonVariant="primary"
			confirmDisabled={!canAdvance}
			onConfirm={handleNext}
			confirmDataTestId="pbj-model-setup-next"
			actionsStart={
				activeStep > 0 ? (
					<SystemButton
						buttonVariant="borderless"
						onClick={() => setActiveStep((previous) => previous - 1)}
						noFullWidthMobile
						data-testid="pbj-model-setup-back"
					>
						{t("pbj_model.setup.back")}
					</SystemButton>
				) : undefined
			}
			body={
				<Box sx={{ pt: 1 }}>
					<Stepper activeStep={activeStep} sx={{ mb: 3 }}>
						{SETUP_STEPS.map((setupStep) => (
							<Step key={setupStep}>
								<StepLabel>{t(SETUP_STEP_LABEL_KEY[setupStep])}</StepLabel>
							</Step>
						))}
					</Stepper>
					{step === "facility" && <SetupFacilityStep draft={draft} onDraftChange={onDraftChange} onManualCmiValidityChange={setManualCmiValid} />}
					{step === "turnover" && <SetupTurnoverStep prediction={prediction} draft={draft} onDraftChange={onDraftChange} />}
					{step === "manualHours" && <SetupManualHoursStep prediction={prediction} startDate={startDate} endDate={endDate} missingJobs={missingJobs} />}
				</Box>
			}
		/>
	);
};
